import type { QueryClient as TanstackQueryClient } from '@tanstack/react-query';
import {
  type AnyQueryClient,
  InfiniteQuery as DefaultInfiniteQuery,
  Query as DefaultQuery,
  InfiniteQueryConfig,
  type InfiniteQueryFlattenConfig,
  type QueryConfig,
  type QueryOptionsParams,
} from 'mobx-tanstack-query';
import { queryClient as defaultQueryClient } from '~/providers/QueryClientProvider';

type QueryClientType = AnyQueryClient | TanstackQueryClient;

/**
 * Wrapper around the {@link DefaultQuery Query} class from {@link https://github.com/js2me/mobx-tanstack-query mobx-tanstack-query}.
 * It is used to create a query that can be used in the store.
 *
 * @param config - The configuration object for the query.
 * @param configFn - The function that returns the configuration object for the query.
 * @returns A new query instance.
 *
 * @example
 * ```ts
 * private readonly _dataQuery = new Query({
 *   queryKey: ['data'],
 *   queryFn: () => {
 *     return Promise.resolve({ data: 'test' });
 *   },
 * });
 * ```
 */
export class Query<
  TQueryFnData = unknown,
  TError = unknown,
  TData = TQueryFnData,
  TQueryData = TQueryFnData,
  TQueryKey extends readonly unknown[] = readonly unknown[],
> extends DefaultQuery<TQueryFnData, TError, TData, TQueryData, TQueryKey> {
  constructor(
    config: Omit<
      QueryConfig<TQueryFnData, TError, TData, TQueryData, TQueryKey>,
      'queryClient'
    > & {
      queryClient?: QueryClientType;
    },
  );

  constructor(
    queryClient: QueryClientType | undefined,
    config: () => QueryOptionsParams<
      TQueryFnData,
      TError,
      TData,
      TQueryData,
      TQueryKey
    >,
  );

  constructor(
    configOrQueryClient:
      | (Omit<
          QueryConfig<TQueryFnData, TError, TData, TQueryData, TQueryKey>,
          'queryClient'
        > & {
          queryClient?: QueryClientType;
        })
      | QueryClientType
      | undefined,
    configFn?: () => QueryOptionsParams<
      TQueryFnData,
      TError,
      TData,
      TQueryData,
      TQueryKey
    >,
  ) {
    if (configFn !== undefined) {
      const queryClient =
        (configOrQueryClient as QueryClientType | undefined) ??
        (defaultQueryClient as unknown as AnyQueryClient);
      super(queryClient as AnyQueryClient, configFn);
      return;
    }

    const config = configOrQueryClient as Omit<
      QueryConfig<TQueryFnData, TError, TData, TQueryData, TQueryKey>,
      'queryClient'
    > & {
      queryClient?: QueryClientType;
    };

    const queryClient = (config.queryClient ??
      (defaultQueryClient as unknown as AnyQueryClient)) as AnyQueryClient;

    super({
      ...config,
      queryClient,
    } as QueryConfig<TQueryFnData, TError, TData, TQueryData, TQueryKey>);
  }
}

/**
 * Wrapper around the {@link DefaultInfiniteQuery InfiniteQuery} class from {@link https://github.com/js2me/mobx-tanstack-query mobx-tanstack-query}.
 * It is used to create a infinite query that can be used in the store.
 *
 * @param config - The configuration object for the query.
 * @param configFn - The function that returns the configuration object for the query.
 * @returns A new infinite query instance.
 *
 * @example
 * ```ts
 * private readonly _dataQuery = new InfiniteQuery({
 *   queryKey: ['data'],
 *   queryFn: () => {
 *     return Promise.resolve({ data: 'test' });
 *   },
 * });
 * ```
 */
export class InfiniteQuery<
  TQueryFnData = unknown,
  TError = unknown,
  TData = TQueryFnData,
  TQueryData = TQueryFnData,
  TQueryKey extends readonly unknown[] = readonly unknown[],
> extends DefaultInfiniteQuery<
  TQueryFnData,
  TError,
  TData,
  TQueryData,
  TQueryKey
> {
  constructor(
    config: Omit<
      InfiniteQueryConfig<TQueryFnData, TError, TData, TQueryData, TQueryKey>,
      'queryClient'
    > & {
      queryClient?: QueryClientType;
    },
  );

  constructor(
    queryClient: QueryClientType | undefined,
    config: () => InfiniteQueryFlattenConfig<
      TQueryFnData,
      TError,
      TData,
      TQueryData,
      TQueryKey
    >,
  );

  constructor(
    configOrQueryClient:
      | (Omit<
          InfiniteQueryConfig<
            TQueryFnData,
            TError,
            TData,
            TQueryData,
            TQueryKey
          >,
          'queryClient'
        > & {
          queryClient?: QueryClientType;
        })
      | QueryClientType
      | undefined,
    configFn?: () => InfiniteQueryFlattenConfig<
      TQueryFnData,
      TError,
      TData,
      TQueryData,
      TQueryKey
    >,
  ) {
    if (configFn !== undefined) {
      const queryClient =
        (configOrQueryClient as QueryClientType | undefined) ??
        (defaultQueryClient as unknown as AnyQueryClient);
      super(queryClient as AnyQueryClient, configFn);
      return;
    }

    const config = configOrQueryClient as Omit<
      InfiniteQueryConfig<TQueryFnData, TError, TData, TQueryData, TQueryKey>,
      'queryClient'
    > & {
      queryClient?: QueryClientType;
    };

    const queryClient = (config.queryClient ??
      (defaultQueryClient as unknown as AnyQueryClient)) as AnyQueryClient;

    super({
      ...config,
      queryClient,
    } as InfiniteQueryConfig<
      TQueryFnData,
      TError,
      TData,
      TQueryData,
      TQueryKey
    >);
  }
}
